import { Permission, type MeetingListFilter } from '@qualicloud/contracts';
import { MeetingStatus } from '@qualicloud/domain';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { MeetingStatusBadge } from '@/components/sgq/sgq-badges';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DatePicker,
  EmptyState,
  Field,
  IconCalendar,
  IconFilter,
  IconPlus,
  IconSearch,
  IconUsers,
  Input,
  LoadingRegion,
  Pagination,
  Popover,
  Select,
  Table,
  cn,
  focusRing,
  parseIso,
  type SortState,
  type TableColumn,
} from '@/design-system';
import { meetingTypesApi, meetingsApi, queryKeys } from '@/lib/api';
import type { MeetingListRow } from '@/lib/api/sgq.types';
import { formatDateTime } from '@/lib/format/datetime';
import { MEETING_STATUS_LABEL } from '@/lib/sgq/presentation';
import { useAuth, useOrganizationTimeZone } from '@/providers/auth-provider';
import type { MeetingSearch } from '@/routes/search-schemas';
import { PageBody, PageHeader } from '@/shell/app-shell';

type SortKey = NonNullable<MeetingListFilter['sortBy']>;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * REUNIÕES — lista e agenda — sgq.md §B.3
 *
 * SACAP é um TIPO de reunião, não a reunião. Enquanto o tipo era um enum de
 * três valores com SACAP como padrão, tela e tipo viraram sinônimos; com o
 * catálogo administrável isso deixou de fazer sentido — a organização pode ter
 * dez tipos, e nenhum deles chamado SACAP.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A SITUAÇÃO É COLUNA, não relógio. No legado o estado da reunião não tinha
 * coluna: era derivado do tempo e de `dt_cancela`/`dt_encerra` por um `CASE` em
 * SQL. Consequência prática: a reunião "acontecia" sozinha quando o horário
 * passava, mesmo sem ninguém comparecer, e não havia como distinguir "ocorreu"
 * de "o horário passou". Aqui abrir a reunião é ato explícito, e
 * `AWAITING_CLOSURE` é o estado de quem deixou o horário passar.
 *
 * O filtro `includeCancelled` preserva o comportamento do legado, que excluía
 * canceladas por padrão (`if(!$canceladas) ... dt_cancela is null`).
 *
 * DUAS VISUALIZAÇÕES da mesma consulta: lista (tabela ordenável, paginada) e
 * agenda (grade mensal). A escolha vai para a URL, então o link compartilhado
 * abre na visão que o remetente estava vendo.
 */
export function MeetingsPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const timeZone = useOrganizationTimeZone();
  const search = useSearch({ strict: false }) as unknown as MeetingSearch;

  const view = search.view === 'calendar' ? 'calendar' : 'list';

  const filter: Partial<MeetingListFilter> = useMemo(
    () => ({
      page: search.page ?? 1,
      // Na agenda o recorte é o mês inteiro: paginar um calendário esconderia
      // reuniões sem que o usuário perceba a ausência.
      pageSize: view === 'calendar' ? 200 : (search.pageSize ?? 50),
      status: search.status as MeetingListFilter['status'],
      meetingTypeIds: search.meetingTypeIds as MeetingListFilter['meetingTypeIds'],
      scope: (search.scope as MeetingListFilter['scope']) ?? 'ALL',
      nonconformityId: search.nonconformityId,
      search: search.search,
      // A URL guarda `AAAA-MM-DD`; o filtro do contrato pede `Date`.
      startsFrom: parseIso(search.startsFrom ?? '') ?? undefined,
      startsTo: parseIso(search.startsTo ?? '') ?? undefined,
      includeCancelled: search.includeCancelled ?? false,
      sortBy: (search.sortBy as SortKey) ?? 'startsAt',
      sortDir: search.sortDir ?? 'desc',
    }),
    [search, view],
  );

  const updateFilter = useCallback(
    (patch: Partial<MeetingSearch>) => {
      void navigate({
        to: '/reunioes',
        search: { ...search, ...patch, page: patch.page ?? 1 } as MeetingSearch,
      });
    },
    [navigate, search],
  );

  const listQuery = useQuery({
    queryKey: queryKeys.meetings.list(filter),
    queryFn: () => meetingsApi.list(filter),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<Array<TableColumn<MeetingListRow, SortKey>>>(
    () => [
      {
        id: 'subject',
        header: 'Assunto',
        sortKey: 'subject',
        width: '30%',
        cell: (row) => (
          <div className="min-w-0">
            <Link
              to="/reunioes/$meetingId"
              params={{ meetingId: row.id }}
              className={cn('block truncate font-medium text-brand hover:underline', focusRing)}
            >
              {row.subject}
            </Link>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              {row.isFollowUp ? (
                <Badge tone="warning" size="sm">
                  Reavaliação de eficácia
                </Badge>
              ) : null}
              {row.effective === false ? (
                <Badge tone="danger" size="sm">
                  Ação não eficaz
                </Badge>
              ) : null}
              {row.effective === true ? (
                <Badge tone="success" size="sm">
                  Ação eficaz
                </Badge>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        id: 'kind',
        header: 'Tipo',
        width: '16%',
        cell: (row) => (
          <span className="text-sm text-content-muted">{row.typeLabel}</span>
        ),
      },
      {
        id: 'startsAt',
        header: 'Início',
        sortKey: 'startsAt',
        width: '14%',
        cell: (row) => (
          <span className="qc-tabular text-sm text-content">
            {formatDateTime(row.startsAt, timeZone)}
          </span>
        ),
      },
      {
        id: 'location',
        header: 'Local',
        width: '12%',
        cell: (row) => <span className="text-sm text-content-muted">{row.location}</span>,
      },
      {
        id: 'participants',
        header: 'Participantes',
        width: '12%',
        cell: (row) => (
          <span className="text-sm text-content-muted qc-tabular">
            <span aria-hidden="true">
              {row.confirmedCount}/{row.participantCount}
            </span>
            <span className="qc-sr-only">
              {row.confirmedCount} de {row.participantCount} participantes confirmaram presença
            </span>
          </span>
        ),
      },
      {
        id: 'nonconformities',
        header: 'Não-conformidades',
        width: '12%',
        align: 'right',
        cell: (row) => (
          <span className="text-sm text-content-muted qc-tabular">
            <span aria-hidden="true">{row.nonconformityCount}</span>
            <span className="qc-sr-only">
              {row.nonconformityCount} não-conformidades vinculadas
            </span>
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Situação',
        sortKey: 'status',
        width: '16%',
        cell: (row) => <MeetingStatusBadge status={row.status} />,
      },
    ],
    [timeZone],
  );

  const activeFilters = countActiveFilters(search);
  const items = listQuery.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Reuniões"
        description="Agenda e histórico. Cada reunião tem um tipo, e é o tipo que define se ela exige vínculo com não-conformidade e avaliação de eficácia."
        actions={
          can(Permission.MEETING_MANAGE) ? (
            <Button asChild variant="primary" leadingIcon={<IconPlus size={15} />}>
              <Link to="/reunioes/nova">Agendar reunião</Link>
            </Button>
          ) : null
        }
      />

      <PageBody>
        <Card padding="sm" className="mb-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <FilterBar
              search={search}
              activeCount={activeFilters}
              onChange={updateFilter}
              onClear={() => void navigate({ to: '/reunioes', search: {} as MeetingSearch })}
            />

            <div role="group" aria-label="Visualização" className="flex items-center gap-1">
              <Button
                size="sm"
                variant={view === 'list' ? 'primary' : 'secondary'}
                aria-pressed={view === 'list'}
                onClick={() => updateFilter({ view: 'list' })}
              >
                Lista
              </Button>
              <Button
                size="sm"
                variant={view === 'calendar' ? 'primary' : 'secondary'}
                aria-pressed={view === 'calendar'}
                leadingIcon={<IconCalendar size={14} />}
                onClick={() => updateFilter({ view: 'calendar' })}
              >
                Agenda
              </Button>
            </div>
          </div>
        </Card>

        {view === 'calendar' ? (
          <MeetingCalendar
            meetings={items}
            loading={listQuery.isPending}
            timeZone={timeZone}
            month={search.startsFrom ?? null}
            onMonthChange={(from, to) => updateFilter({ startsFrom: from, startsTo: to })}
          />
        ) : (
          <>
            <Table<MeetingListRow, SortKey>
              caption="Reuniões da organização. Colunas: assunto, tipo, início, local, participantes confirmados, não-conformidades vinculadas e situação."
              columns={columns}
              rows={items}
              rowKey={(row) => row.id}
              loading={listQuery.isPending}
              sort={{ by: (filter.sortBy ?? 'startsAt') as SortKey, dir: filter.sortDir ?? 'desc' }}
              onSortChange={(sort: SortState<SortKey>) =>
                updateFilter({ sortBy: sort.by, sortDir: sort.dir })
              }
              empty={
                <EmptyState
                  icon={<IconCalendar size={22} />}
                  title={
                    activeFilters > 0
                      ? 'Nenhuma reunião corresponde aos filtros'
                      : 'Nenhuma reunião agendada'
                  }
                  description={
                    activeFilters > 0
                      ? 'Ajuste ou limpe os filtros. Lembre-se de que reuniões canceladas ficam fora da lista por padrão.'
                      : 'Agende uma SACAP para tratar as não-conformidades procedentes. Uma SACAP precisa estar vinculada a ao menos uma não-conformidade — sem vínculo, não há o que analisar.'
                  }
                  action={
                    activeFilters > 0 ? (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          void navigate({ to: '/reunioes', search: {} as MeetingSearch })
                        }
                      >
                        Limpar filtros
                      </Button>
                    ) : can(Permission.MEETING_MANAGE) ? (
                      <Button asChild variant="primary">
                        <Link to="/reunioes/nova">Agendar a primeira reunião</Link>
                      </Button>
                    ) : undefined
                  }
                />
              }
            />

            {listQuery.data && listQuery.data.total > 0 ? (
              <div className="rounded-b-lg border-x border-b border-border bg-surface">
                <Pagination
                  page={listQuery.data.page}
                  pageSize={listQuery.data.pageSize}
                  total={listQuery.data.total}
                  itemNoun="reuniões"
                  onPageChange={(page) => updateFilter({ page })}
                  onPageSizeChange={(pageSize) => updateFilter({ pageSize, page: 1 })}
                />
              </div>
            ) : null}
          </>
        )}
      </PageBody>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Agenda mensal
// ─────────────────────────────────────────────────────────────────────────────

const WEEKDAYS_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const;
const WEEKDAYS_LONG = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
] as const;
const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

/**
 * Agenda mensal.
 *
 * É uma TABELA de verdade — `<caption>`, `<th scope="col">` com o nome do dia
 * da semana por extenso, uma linha por semana — e não uma grade de `<div>`s.
 * Assim quem usa leitor de tela navega por células com a mesma orientação
 * espacial que a grade dá visualmente, e cada reunião é um link tabulável com
 * nome completo ("SACAP de 14/03 às 09:00 — Retrabalho na linha 2").
 */
function MeetingCalendar({
  meetings,
  loading,
  timeZone,
  month,
  onMonthChange,
}: {
  readonly meetings: readonly MeetingListRow[];
  readonly loading: boolean;
  readonly timeZone: string;
  readonly month: string | null;
  readonly onMonthChange: (from: string, to: string) => void;
}) {
  const anchor = useMemo(() => {
    const base = parseIso(month ?? '') ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  }, [month]);

  const weeks = useMemo(() => buildMonthGrid(anchor), [anchor]);

  const byDay = useMemo(() => {
    const map = new Map<string, MeetingListRow[]>();
    for (const meeting of meetings) {
      const key = isoDayInZone(meeting.startsAt, timeZone);
      const bucket = map.get(key);
      if (bucket) bucket.push(meeting);
      else map.set(key, [meeting]);
    }
    for (const bucket of map.values()) {
      bucket.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    }
    return map;
  }, [meetings, timeZone]);

  const shiftMonth = (delta: number) => {
    const next = new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1);
    const last = new Date(next.getFullYear(), next.getMonth() + 1, 0);
    onMonthChange(toIsoDay(next), toIsoDay(last));
  };

  return (
    <Card padding="none">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-3">
        {/*
          O anúncio da troca de mês sai da região viva abaixo, que já traz o mês
          e a contagem. Um `aria-live` também no título faria o leitor de tela
          ler o mês duas vezes a cada navegação.
        */}
        <h2 className="text-base font-semibold capitalize text-content">
          {MONTHS[anchor.getMonth()]} de {anchor.getFullYear()}
        </h2>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => shiftMonth(-1)}>
            Mês anterior
          </Button>
          <Button size="sm" variant="secondary" onClick={() => shiftMonth(1)}>
            Próximo mês
          </Button>
        </div>
      </div>

      {/*
        A região viva fica MONTADA nos dois estados: `aria-live` inserido no
        mesmo instante que o texto não dispara anúncio nenhum — o leitor de tela
        só observa mudanças dentro de uma região que já existia. Dentro dela vai
        apenas o status; jogar a grade inteira aqui faria o leitor ler as 42
        células a cada carga e a cada troca de mês.
      */}
      <LoadingRegion loading={loading} label="Carregando a agenda…">
        {loading ? (
          <p aria-hidden="true" className="px-4 py-10 text-center text-sm text-content-subtle">
            Carregando a agenda…
          </p>
        ) : (
          <p className="qc-sr-only">
            Agenda de {MONTHS[anchor.getMonth()]} de {anchor.getFullYear()} carregada:{' '}
            {meetings.length === 1 ? '1 reunião' : `${meetings.length} reuniões`}.
          </p>
        )}
      </LoadingRegion>

      {loading ? null : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed border-collapse text-sm">
            <caption className="qc-sr-only">
              Agenda de reuniões de {MONTHS[anchor.getMonth()]} de {anchor.getFullYear()}. Cada
              célula traz o dia do mês e as reuniões agendadas naquele dia.
            </caption>
            <thead>
              <tr className="border-b border-border bg-surface-sunken">
                {WEEKDAYS_SHORT.map((day, index) => (
                  <th
                    key={day}
                    scope="col"
                    className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-content-muted"
                  >
                    <span aria-hidden="true">{day}</span>
                    <span className="qc-sr-only">{WEEKDAYS_LONG[index]}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, weekIndex) => (
                <tr key={weekIndex} className="border-b border-border-subtle last:border-b-0">
                  {week.map((date) => {
                    const iso = toIsoDay(date);
                    const inMonth = date.getMonth() === anchor.getMonth();
                    const dayMeetings = byDay.get(iso) ?? [];
                    return (
                      <td
                        key={iso}
                        className={cn(
                          // A célula cresceu junto com a tipografia do link: três
                          // reuniões no mesmo dia cabem em `h-32` sem apertar o
                          // texto abaixo do piso da escala.
                          'h-32 border-r border-border-subtle p-1.5 align-top last:border-r-0',
                          !inMonth && 'bg-surface-sunken',
                        )}
                      >
                        <p
                          className={cn(
                            'mb-1 text-xs qc-tabular',
                            inMonth ? 'text-content' : 'text-content-subtle',
                          )}
                        >
                          <span aria-hidden="true">{date.getDate()}</span>
                          <span className="qc-sr-only">
                            {date.getDate()} de {MONTHS[date.getMonth()]}
                            {dayMeetings.length === 0
                              ? ', sem reuniões'
                              : `, ${dayMeetings.length} ${dayMeetings.length === 1 ? 'reunião' : 'reuniões'}`}
                          </span>
                        </p>
                        {dayMeetings.length > 0 ? (
                          <ul className="flex flex-col gap-1">
                            {dayMeetings.map((meeting) => (
                              <li key={meeting.id}>
                                <Link
                                  to="/reunioes/$meetingId"
                                  params={{ meetingId: meeting.id }}
                                  className={cn(
                                    'block truncate rounded px-1.5 py-1 text-xs',
                                    'bg-brand-subtle text-brand hover:underline',
                                    focusRing,
                                  )}
                                >
                                  <span aria-hidden="true">
                                    {formatDateTime(meeting.startsAt, timeZone).slice(-5)}{' '}
                                    {meeting.subject}
                                  </span>
                                  <span className="qc-sr-only">
                                    {meeting.subject}, {formatDateTime(meeting.startsAt, timeZone)},{' '}
                                    {MEETING_STATUS_LABEL[meeting.status]}
                                  </span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && meetings.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={<IconCalendar size={20} />}
          title="Nenhuma reunião neste período"
          description="Navegue entre os meses ou ajuste os filtros. A agenda mostra apenas as reuniões que você pode ver."
        />
      ) : null}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Filtros
// ─────────────────────────────────────────────────────────────────────────────

function FilterBar({
  search,
  activeCount,
  onChange,
  onClear,
}: {
  readonly search: MeetingSearch;
  readonly activeCount: number;
  readonly onChange: (patch: Partial<MeetingSearch>) => void;
  readonly onClear: () => void;
}) {
  const [term, setTerm] = useState(search.search ?? '');

  /*
   * As opções de tipo vêm do CATÁLOGO da organização, não de um enum. Uma lista
   * fixa filtraria por tipos que o cliente não usa e esconderia os que ele
   * criou. Só os ativos: filtrar por tipo aposentado é caso de nicho, e a lista
   * curta serve melhor a quem filtra todo dia.
   */
  const typesQuery = useQuery({
    queryKey: queryKeys.meetingTypes.all(),
    queryFn: () => meetingTypesApi.list(),
  });
  const meetingTypes = typesQuery.data ?? [];

  return (
    <section aria-label="Filtros da lista de reuniões" className="flex flex-1 flex-wrap items-end gap-3">
      <form
        role="search"
        className="min-w-[14rem] flex-1"
        onSubmit={(event) => {
          event.preventDefault();
          onChange({ search: term.trim() || undefined });
        }}
      >
        <label htmlFor="meeting-search" className="qc-sr-only">
          Buscar por assunto da reunião
        </label>
        <Input
          id="meeting-search"
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Buscar por assunto…"
          leadingIcon={<IconSearch size={15} />}
        />
      </form>

      <div className="w-56">
        <Field label="Escopo">
          <Select
            value={search.scope ?? 'ALL'}
            onValueChange={(value) => onChange({ scope: value })}
            options={[
              { value: 'ALL', label: 'Todas as que posso ver' },
              { value: 'ORGANIZER', label: 'Que eu organizei' },
              {
                value: 'PARTICIPANT',
                label: 'Em que fui convidado',
                description: 'Reuniões em que estou na lista de participantes.',
              },
            ]}
          />
        </Field>
      </div>

      <Popover
        width="22rem"
        title="Filtros avançados"
        showClose
        trigger={
          <Button variant="secondary" leadingIcon={<IconFilter size={15} />}>
            Mais filtros
            {activeCount > 0 ? (
              <Badge tone="brand" size="sm" className="ml-1">
                {activeCount}
              </Badge>
            ) : null}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label="Tipo de reunião">
            {/*
              As opções vêm do CATÁLOGO da organização, não de um enum: o cliente
              cria os próprios tipos, e uma lista fixa aqui filtraria por tipos
              que ele não usa e esconderia os que usa.
            */}
            <Select
              value={search.meetingTypeIds?.[0] ?? '__all__'}
              onValueChange={(value) =>
                onChange({ meetingTypeIds: value === '__all__' ? undefined : [value] })
              }
              options={[
                { value: '__all__', label: 'Todos os tipos' },
                ...meetingTypes.map((type) => ({ value: type.id, label: type.name })),
              ]}
            />
          </Field>

          <Field label="Situação">
            <Select
              value={search.status?.[0] ?? '__all__'}
              onValueChange={(value) =>
                onChange({ status: value === '__all__' ? undefined : [value] })
              }
              options={[
                { value: '__all__', label: 'Todas as situações' },
                ...Object.values(MeetingStatus).map((status) => ({
                  value: status,
                  label: MEETING_STATUS_LABEL[status],
                })),
              ]}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Início a partir de">
              <DatePicker
                value={search.startsFrom ?? ''}
                onValueChange={(iso) => onChange({ startsFrom: iso || undefined })}
              />
            </Field>
            <Field label="Início até">
              <DatePicker
                value={search.startsTo ?? ''}
                onValueChange={(iso) => onChange({ startsTo: iso || undefined })}
              />
            </Field>
          </div>

          <Checkbox
            checked={search.includeCancelled ?? false}
            onCheckedChange={(checked) => onChange({ includeCancelled: checked || undefined })}
            label="Incluir reuniões canceladas"
            description="Por padrão as canceladas ficam fora da lista."
          />

          {activeCount > 0 ? (
            <Button variant="ghost" onClick={onClear} className="self-start">
              Limpar todos os filtros
            </Button>
          ) : null}
        </div>
      </Popover>

      {search.nonconformityId ? (
        <Badge tone="brand" icon={<IconUsers size={12} />}>
          Filtrado por uma não-conformidade
        </Badge>
      ) : null}
    </section>
  );
}

function countActiveFilters(search: MeetingSearch): number {
  let count = 0;
  if (search.status?.length) count += 1;
  if (search.meetingTypeIds?.length) count += 1;
  if (search.search) count += 1;
  if (search.startsFrom) count += 1;
  if (search.startsTo) count += 1;
  if (search.includeCancelled) count += 1;
  if (search.nonconformityId) count += 1;
  if (search.scope && search.scope !== 'ALL') count += 1;
  return count;
}

// ─────────────────────────────────────────────────────────────────────────────
// Datas da agenda
// ─────────────────────────────────────────────────────────────────────────────

function toIsoDay(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Dia civil do instante NO FUSO DA ORGANIZAÇÃO.
 *
 * Sem isto, uma reunião das 22h em UTC-3 cairia no dia seguinte na grade, que é
 * o mesmo gênero de erro que `new Date('2026-03-14')` produz com datas puras.
 */
function isoDayInZone(instant: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(instant));
}

function buildMonthGrid(month: Date): Date[][] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay());
  const weeks: Date[][] = [];
  for (let week = 0; week < 6; week += 1) {
    const days: Date[] = [];
    for (let day = 0; day < 7; day += 1) {
      days.push(
        new Date(start.getFullYear(), start.getMonth(), start.getDate() + week * 7 + day),
      );
    }
    weeks.push(days);
  }
  return weeks;
}
